import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  TextField,
  Autocomplete,
  Box,
  Card,
  Grid,
  styled,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FilledInput,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';
import SaveIcon from '@mui/icons-material/Save';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  AddOnCategory,
  AddOnItem,
  ClientApp,
  PrinterProfile
} from '../../../../services/apiService/response-models';
import DropdownMultiSelect from 'src/components/dropdown-multi-select';
import {
  getAllAddOnCategories,
  getDeviceList,
  getPrinterProfileList
} from '../../../../services/apiService';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import readTenantCodeFromUrl from 'src/utils/readTenantCodeFromUrl';

const EditorWrapper = styled(Box)(
  ({ theme }) => `

    .ql-editor {
      min-height: 100px;
    }

    .ql-snow .ql-picker {
      color: ${theme.colors.alpha.black[100]};
    }

    .ql-snow .ql-stroke {
      stroke: ${theme.colors.alpha.black[100]};
    }

    .ql-toolbar.ql-snow {
      border-top-left-radius: ${theme.general.borderRadius};
      border-top-right-radius: ${theme.general.borderRadius};
    }

    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${theme.colors.alpha.black[30]};
    }

    .ql-container.ql-snow {
      border-bottom-left-radius: ${theme.general.borderRadius};
      border-bottom-right-radius: ${theme.general.borderRadius};
    }

    &:hover {
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: ${theme.colors.alpha.black[50]};
      }
    }
`
);
const CopyContentIcon = styled(ContentCopyTwoToneIcon)(
  ({ theme }) => `
    margin-left: ${theme.spacing(1)};
    cursor: pointer;
    color: ${theme.palette.primary.main};
    font-size: 30px;
  `
);

// Helper function to generate a random secret
function generateSecret(length = 44) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function GeneralSection(props: {
  model: ClientApp | null;
  isSaveInProgress: boolean;
  onSaveClick: () => void;
  onSave: (updatedModel: ClientApp) => Promise<boolean>;
}) {
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate();
  const [isEdit] = React.useState(props.model != null && props.model.id);
  const [categories, setCategories] = useState<ClientApp[]>([]);
  const deviceTypes = ['pos', 'Kiosk', 'server-go'];
  const [printerProfiles, setPrinterProfiles] = useState<PrinterProfile[]>();
  const isNew = !props.model || !props.model.id;

  const fetchPrinterProfiles = async () => {
    try {
      const response = await getPrinterProfileList();
      setPrinterProfiles(response.data || []);
    } catch (err) {
      console.error('Failed to fetch printer profiles:', err);
    }
  };

  useEffect(() => {
    getByCategories();
    fetchPrinterProfiles(); // fetch printer profiles on mount
  }, []);

  const getByCategories = async () => {
    try {
      const response = await getDeviceList();
      setCategories(response.data.filter((o) => o.isActive));
    } catch (ex) {
      console.error(ex);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Card
      sx={{
        p: 3
      }}
    >
      <Formik
        initialValues={{
          deviceId: props.model?.deviceId || '',
          appName: props.model?.appName || '',
          tenantCode: props.model?.tenantCode || readTenantCodeFromUrl() || '',
          deviceType: props.model?.deviceType || '',
          printerProfile: props.model?.printerProfile || null,
          clientApiKey: props.model?.clientApiKey || '',
          clientApiSecrete: isNew ? generateSecret() : (props.model?.clientApiSecrete || ''),
          isActive: props.model?.isActive ?? true
        }}
        validationSchema={Yup.object().shape({
          deviceType: Yup.string().required(t('Device type is required')),
        })}
        onSubmit={async (
          values,
          { resetForm, setErrors, setStatus, setSubmitting }
        ) => {
          try {
            const payload = {
              deviceId: values.deviceId,
              appName: values.appName,
              tenantCode: values.tenantCode,
              deviceType: values.deviceType,
              printerProfile: values.printerProfile,
              clientApiKey: values.clientApiKey,
              clientApiSecrete: values.clientApiSecrete,
              isActive: values.isActive
            } as unknown as ClientApp;

            const isSuccess = await props.onSave(payload);
            if (isSuccess) {
              resetForm();
              setStatus({ success: true });
            }
          } catch (err) {
            console.error(err);
            setStatus({ success: false });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="appName"
                  label="Device Name"
                  placeholder={t('Enter Device Name...')}
                  variant="outlined"
                  error={Boolean(touched.appName && errors.appName)}
                  helperText={touched.appName && errors.appName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.appName}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="tenantCode"
                  label="Tenant Code"
                  variant="outlined"
                  value={values.tenantCode}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      color: 'rgba(0, 0, 0, 0.38)'
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: 'rgba(0, 0, 0, 0.38)'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  options={deviceTypes}
                  value={values.deviceType}
                  onChange={(event, newValue) => {
                    setFieldValue('deviceType', newValue || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={t('Driver')}
                      error={Boolean(touched.deviceType && errors.deviceType)}
                      helperText={touched.deviceType && errors.deviceType}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    name="clientApiKey"
                    label="API Key"
                    variant="outlined"
                    placeholder={t('Enter API Key...')}
                    value={values.clientApiKey}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.clientApiKey && errors.clientApiKey)}
                    helperText={touched.clientApiKey && errors.clientApiKey}
                  />
                  {/* <CopyContentIcon /> */}
                </Box>
                {/* <Box sx={{ marginTop: 1 }}>
                  <Button variant="outlined">Regenerate</Button>
                </Box> */}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    name="clientApiSecrete"
                    label="API Secret"
                    variant="outlined"
                    placeholder={t('API Secret')}
                    value={values.clientApiSecrete}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: 'rgba(0, 0, 0, 0.38)'
                      }
                    }}
                  />
                  {/* <CopyContentIcon /> */}
                  <Button
                    variant="outlined"
                    sx={{ marginLeft: 1, whiteSpace: 'nowrap' }}
                    onClick={() => setFieldValue('clientApiSecrete', generateSecret())}
                    type="button"
                  >
                    Regenerate
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="deviceId"
                  label="Device ID"
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.deviceId}
                  error={Boolean(touched.deviceId && errors.deviceId)}
                  helperText={touched.deviceId && errors.deviceId}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
          
        </Grid> */}
              {/* Device ID Field */}

              {/* Printer Profile Field (Optional) */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  options={printerProfiles || []}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.name
                  }
                  value={
                    printerProfiles?.find(
                      (p) => p.id === values.printerProfile
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue !== 'string') {
                      setFieldValue('printerProfile', newValue.id);
                    } else {
                      setFieldValue('printerProfile', '');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={t('Printer Profile')}
                      error={Boolean(
                        touched.printerProfile && errors.printerProfile
                      )}
                      helperText={
                        touched.printerProfile && errors.printerProfile
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      name="isActive"
                      // color={'primary' : 'secondary'}
                      // disabled={!props.isEdit}
                      onChange={handleChange}
                      checked={values.isActive}
                    />
                  }
                  label={t('Is Active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  sx={{ mr: 2 }}
                  type="button"
                  onClick={goBack}
                  variant="text"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={() => props.onSaveClick?.()}
                  variant="contained"
                  startIcon={
                    isSubmitting || props.isSaveInProgress ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isSubmitting || props.isSaveInProgress}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Card>
  );
}

export default GeneralSection;
